import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { TermosConteudoCompleto } from './TermosConteudoCompleto';
import { Loader2, CheckCircle2 } from 'lucide-react';

const SCROLL_THRESHOLD = 50; // pixels from bottom to consider "scrolled"

interface TermosAceiteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAccept: () => void | Promise<void>;
  loading?: boolean;
  /** Quando true: aceite obrigatório no login (nova revisão ou primeiro aceite). Oculta Cancelar e exibe "Aceitar e continuar". */
  blocking?: boolean;
}

export function TermosAceiteModal({
  open,
  onOpenChange,
  onAccept,
  loading = false,
  blocking = false,
}: TermosAceiteModalProps) {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setScrolledToBottom(false);
      scrollRef.current?.scrollTo(0, 0);
    }
  }, [open]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    setScrolledToBottom(distanceFromBottom <= SCROLL_THRESHOLD);
  }, []);

  const handleAccept = async () => {
    if (!scrolledToBottom) return;
    await onAccept();
    onOpenChange(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (blocking && !nextOpen) return; // não permite fechar sem aceitar
    if (!loading) {
      if (!nextOpen) setScrolledToBottom(false);
      onOpenChange(nextOpen);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle>Termos de Uso e Política de Privacidade</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Por favor, leia os termos completos. Role até o final para habilitar o botão de aceite.
          </p>
        </DialogHeader>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto border rounded-lg p-4 bg-muted/30 max-h-[50vh] min-h-[200px]"
        >
          <TermosConteudoCompleto />
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {!blocking && (
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              Cancelar
            </Button>
          )}
          <Button
            onClick={handleAccept}
            disabled={!scrolledToBottom || loading}
            className="w-full sm:w-auto"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {blocking ? 'Registrando aceite...' : 'Ativando conta...'}
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                {blocking ? 'Aceitar e continuar' : 'Aceitar e Ativar Conta'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
